/**
 * Register van locatie-specifieke afbeeldingen.
 *
 * Elke stad/gemeente met een eigen, herkenbare afbeelding staat hieronder met
 * een unieke bestandsnaam, alt-tekst, image title en caption. Locaties zonder
 * eigen asset krijgen bewust GEEN afbeelding: er is geen generieke fallback,
 * zodat pagina's nooit dezelfde afbeelding of alt-tekst delen.
 */

export type LocationImage = {
  /** Absoluut pad naar de 1200x630 WebP-variant. */
  src: string;
  /** Responsive varianten (480/800/1200 px breed). */
  srcSet: string;
  alt: string;
  title: string;
  caption: string;
  width: number;
  height: number;
};

type ImageCopy = { alt: string; title: string; caption: string };

const WIDTH = 1200;
const HEIGHT = 630;

/** Steden en dorpen met een eigen afbeelding (/taxi/<slug>). */
export const CITY_IMAGE_COPY: Record<string, ImageCopy> = {
  barendrecht: {
    alt: "Taxi Barendrecht bij de watertoren",
    title: "Taxi Barendrecht met vaste prijs",
    caption: "Taxi Barendrecht: ophalen bij de watertoren, het centrum of uw eigen adres.",
  },
  amsterdam: {
    alt: "Taxi in Amsterdam langs een gracht met grachtenpanden",
    title: "Taxi Amsterdam met vaste prijs",
    caption: "Vanaf de Amsterdamse grachten rijden wij met een vaste prijs naar Schiphol.",
  },
  rotterdam: {
    alt: "Taxi in Rotterdam bij de Erasmusbrug en de skyline",
    title: "Taxi Rotterdam met vaste prijs",
    caption: "Rotterdam: ophalen aan de Maas en zonder omrijkosten naar de luchthaven.",
  },
  "den-haag": {
    alt: "Taxi in Den Haag bij het Binnenhof",
    title: "Taxi Den Haag met vaste prijs",
    caption: "In Den Haag halen wij u op bij het Binnenhof, Scheveningen of uw eigen adres.",
  },
  utrecht: {
    alt: "Taxi in Utrecht bij de Domtoren en de Oudegracht",
    title: "Taxi Utrecht met vaste prijs",
    caption: "Utrecht ligt centraal: naar Schiphol rijdt u in ongeveer drie kwartier.",
  },
  dordrecht: {
    alt: "Taxi in Dordrecht bij de Grote Kerk aan het water",
    title: "Taxi Dordrecht met vaste prijs",
    caption: "Vanuit het oude centrum van Dordrecht rijden wij dagelijks naar Schiphol.",
  },
  gouda: {
    alt: "Taxi in Gouda op de Markt bij het oude stadhuis",
    title: "Taxi Gouda met vaste prijs",
    caption: "In Gouda stappen reizigers vaak bij de Markt of het station in.",
  },
  haarlem: {
    alt: "Taxi in Haarlem bij de Grote Markt en de Sint-Bavokerk",
    title: "Taxi Haarlem met vaste prijs",
    caption: "Haarlem ligt vlak bij Schiphol; een korte rit met een vaste prijs.",
  },
  leiden: {
    alt: "Taxi in Leiden langs een gracht met de Koornbrug",
    title: "Taxi Leiden met vaste prijs",
    caption: "In Leiden rijden wij van de binnenstad en de universiteitswijk naar de luchthaven.",
  },
  delft: {
    alt: "Taxi in Delft bij de Markt met de Nieuwe Kerk",
    title: "Taxi Delft met vaste prijs",
    caption: "Delft: ophalen bij de Markt, Delft Zuid of TU-campus tegen een vaste prijs.",
  },
  alkmaar: {
    alt: "Taxi in Alkmaar bij de Waag op het Waagplein",
    title: "Taxi Alkmaar met vaste prijs",
    caption: "Vanuit Alkmaar rijdt u in ongeveer een half uur naar Schiphol.",
  },
  amersfoort: {
    alt: "Taxi in Amersfoort bij de Onze Lieve Vrouwetoren",
    title: "Taxi Amersfoort met vaste prijs",
    caption: "Amersfoort: een vaste prijs naar Schiphol, ook bij vroege vluchten.",
  },
  hoorn: {
    alt: "Taxi in Hoorn bij de haven met de Hoofdtoren",
    title: "Taxi Hoorn met vaste prijs",
    caption: "In Hoorn beginnen veel ritten aan de historische haven of bij het station.",
  },
  zaandam: {
    alt: "Taxi in Zaandam bij de karakteristieke groene houten huizen",
    title: "Taxi Zaandam met vaste prijs",
    caption: "Zaandam ligt op een kwartier rijden van Schiphol.",
  },
  hilversum: {
    alt: "Taxi in Hilversum in een groene laan bij het mediapark",
    title: "Taxi Hilversum met vaste prijs",
    caption: "Hilversum: zakelijke ritten naar Schiphol met vaste prijs en stille aankomst.",
  },
  schiedam: {
    alt: "Taxi in Schiedam bij de hoge molens aan de Noordvest",
    title: "Taxi Schiedam met vaste prijs",
    caption: "Schiedam ligt naast Rotterdam The Hague Airport; ook nachtritten mogelijk.",
  },
  vlaardingen: {
    alt: "Taxi in Vlaardingen aan de Oude Haven",
    title: "Taxi Vlaardingen met vaste prijs",
    caption: "Vanuit Vlaardingen rijden wij via de A20 naar Schiphol en Rotterdam.",
  },
  zoetermeer: {
    alt: "Taxi in Zoetermeer in een moderne woonwijk",
    title: "Taxi Zoetermeer met vaste prijs",
    caption: "Zoetermeer: ophalen voor de deur in Rokkeveen, Meerzicht of Seghwaert.",
  },
  middelburg: {
    alt: "Taxi in Middelburg bij de Lange Jan en de abdij",
    title: "Taxi Middelburg met vaste prijs",
    caption: "Uit Middelburg is Schiphol een lange rit; onze prijs staat vooraf vast.",
  },
  vlissingen: {
    alt: "Taxi in Vlissingen op de boulevard aan de Westerschelde",
    title: "Taxi Vlissingen met vaste prijs",
    caption: "Vlissingen: vroeg ophalen voor ochtendvluchten, met vaste prijs.",
  },
  goes: {
    alt: "Taxi in Goes bij de Grote Markt met de Maria Magdalenakerk",
    title: "Taxi Goes met vaste prijs",
    caption: "Goes is het knooppunt van Zuid-Beveland voor luchthavenvervoer.",
  },
  zierikzee: {
    alt: "Taxi in Zierikzee bij de Nobelpoort",
    title: "Taxi Zierikzee met vaste prijs",
    caption: "Zierikzee: rustige aankomst op Schiphol vanaf Schouwen-Duiveland.",
  },
  schoonhoven: {
    alt: "Taxi in Schoonhoven bij de Veerpoort aan de Lek",
    title: "Taxi Schoonhoven met vaste prijs",
    caption: "In Schoonhoven halen wij u op aan de Lek of in de zilverstad zelf.",
  },
  kinderdijk: {
    alt: "Taxi bij Kinderdijk met de molens langs de dijk",
    title: "Taxi Kinderdijk met vaste prijs",
    caption: "Kinderdijk: veel gasten reizen hier van hotel naar Schiphol of Rotterdam.",
  },
  volendam: {
    alt: "Taxi in Volendam aan de dijk met de vissershaven",
    title: "Taxi Volendam met vaste prijs",
    caption: "Volendam: geregeld ritten van hotels aan de dijk naar Schiphol.",
  },
  spijkenisse: {
    alt: "Taxi in Spijkenisse in een woonstraat bij het centrum",
    title: "Taxi Spijkenisse met vaste prijs",
    caption: "Spijkenisse: ophalen bij de Boekenberg, een woonwijk of uw eigen adres.",
  },
  "capelle-aan-den-ijssel": {
    alt: "Taxi in Capelle aan den IJssel langs de IJssel",
    title: "Taxi Capelle aan den IJssel met vaste prijs",
    caption: "Capelle aan den IJssel ligt aan de rand van Rotterdam; de prijs staat vooraf vast.",
  },
  ridderkerk: {
    alt: "Taxi in Ridderkerk in een straat bij het centrum",
    title: "Taxi Ridderkerk met vaste prijs",
    caption: "Ridderkerk: via de A16 rijden wij naar Schiphol en Rotterdam The Hague Airport.",
  },
  zwijndrecht: {
    alt: "Taxi in Zwijndrecht bij de oever van de Oude Maas",
    title: "Taxi Zwijndrecht met vaste prijs",
    caption: "In Zwijndrecht halen wij u op bij het station of voor uw eigen deur.",
  },
  "berkel-en-rodenrijs": {
    alt: "Taxi in Berkel en Rodenrijs in een woonwijk bij het dorpscentrum",
    title: "Taxi Berkel en Rodenrijs met vaste prijs",
    caption: "Berkel en Rodenrijs ligt dicht bij Rotterdam The Hague Airport.",
  },
  hellevoetsluis: {
    alt: "Taxi in Hellevoetsluis bij de vesting en de haven",
    title: "Taxi Hellevoetsluis met vaste prijs",
    caption: "Vanuit Hellevoetsluis rijden wij ook vroeg in de ochtend naar Schiphol.",
  },
  "krimpen-aan-den-ijssel": {
    alt: "Taxi in Krimpen aan den IJssel bij de dijk langs de IJssel",
    title: "Taxi Krimpen aan den IJssel met vaste prijs",
    caption: "Krimpen aan den IJssel: ophalen aan de dijk of in de woonwijken.",
  },
  papendrecht: {
    alt: "Taxi in Papendrecht langs de Merwede",
    title: "Taxi Papendrecht met vaste prijs",
    caption: "Papendrecht ligt aan de Merwede; naar Schiphol rijdt u in circa een uur.",
  },
  rhoon: {
    alt: "Taxi in Rhoon in een straat bij het dorpscentrum",
    title: "Taxi Rhoon met vaste prijs",
    caption: "In Rhoon stappen reizigers vaak in bij het metrostation of thuis.",
  },
  "hendrik-ido-ambacht": {
    alt: "Taxi in Hendrik-Ido-Ambacht in een woonstraat",
    title: "Taxi Hendrik-Ido-Ambacht met vaste prijs",
    caption: "Hendrik-Ido-Ambacht: rustig ophalen voor de deur, ook 's nachts geboekt vooraf.",
  },
  poortugaal: {
    alt: "Taxi in Poortugaal in een dorpsstraat met bakstenen huizen",
    title: "Taxi Poortugaal met vaste prijs",
    caption: "Poortugaal ligt op Albrandswaard, dicht bij Rotterdam The Hague Airport.",
  },
  maassluis: {
    alt: "Taxi in Maassluis bij de haven en de Groote Kerk",
    title: "Taxi Maassluis met vaste prijs",
    caption: "Maassluis: via de A20 rijden wij naar Schiphol met een vaste prijs.",
  },
  sliedrecht: {
    alt: "Taxi in Sliedrecht langs de Merwedestraat aan het water",
    title: "Taxi Sliedrecht met vaste prijs",
    caption: "Sliedrecht ligt aan de Merwede; ophalen gebeurt op uw eigen adres.",
  },
  alblasserdam: {
    alt: "Taxi in Alblasserdam bij de dijk vlak bij Kinderdijk",
    title: "Taxi Alblasserdam met vaste prijs",
    caption: "Alblasserdam: veel ritten van hotel of woonadres naar Schiphol.",
  },
  brielle: {
    alt: "Taxi in Brielle in de historische vesting",
    title: "Taxi Brielle met vaste prijs",
    caption: "Brielle: vanuit de vesting rijden wij naar Rotterdam en Schiphol.",
  },
  "nieuwerkerk-aan-den-ijssel": {
    alt: "Taxi in Nieuwerkerk aan den IJssel in een woonstraat bij het water",
    title: "Taxi Nieuwerkerk aan den IJssel met vaste prijs",
    caption: "Nieuwerkerk aan den IJssel ligt tussen Rotterdam en Gouda.",
  },
  pijnacker: {
    alt: "Taxi in Pijnacker in een straat bij het dorpscentrum",
    title: "Taxi Pijnacker met vaste prijs",
    caption: "Pijnacker: ophalen bij het centrum, Klapwijk of uw eigen adres.",
  },
  bergschenhoek: {
    alt: "Taxi in Bergschenhoek in een woonwijk bij het centrum",
    title: "Taxi Bergschenhoek met vaste prijs",
    caption: "Bergschenhoek ligt in Lansingerland, kort bij Rotterdam The Hague Airport.",
  },
  "oud-beijerland": {
    alt: "Taxi in Oud-Beijerland bij de haven in het centrum",
    title: "Taxi Oud-Beijerland met vaste prijs",
    caption: "Oud-Beijerland is het knooppunt van de Hoeksche Waard voor luchthavenvervoer.",
  },
  rozenburg: {
    alt: "Taxi in Rozenburg in een woonstraat bij de haven",
    title: "Taxi Rozenburg met vaste prijs",
    caption: "Rozenburg: ophalen voor de deur, ook voor ploegdiensten in de haven.",
  },
  "pernis-rotterdam": {
    alt: "Taxi in Pernis in een dorpsstraat bij het Rotterdamse havengebied",
    title: "Taxi Pernis met vaste prijs",
    caption: "Pernis ligt midden in het havengebied; wij rijden hier dag en avond.",
  },
  zevenhuizen: {
    alt: "Taxi in Zevenhuizen in een dorpsstraat bij het water",
    title: "Taxi Zevenhuizen met vaste prijs",
    caption: "Zevenhuizen: landelijk gelegen, ophalen altijd op uw eigen adres.",
  },
  bleiswijk: {
    alt: "Taxi in Bleiswijk in een dorpsstraat met bakstenen huizen",
    title: "Taxi Bleiswijk met vaste prijs",
    caption: "Bleiswijk ligt in Lansingerland, vlak bij de A12 richting Schiphol.",
  },
  oostvoorne: {
    alt: "Taxi in Oostvoorne bij de duinen en het dorp",
    title: "Taxi Oostvoorne met vaste prijs",
    caption: "Oostvoorne: vanaf de duinrand rijden wij naar Rotterdam en Schiphol.",
  },
  strijen: {
    alt: "Taxi in Strijen in een dorpsstraat bij de kerk",
    title: "Taxi Strijen met vaste prijs",
    caption: "Strijen ligt in de Hoeksche Waard; de prijs weet u vooraf.",
  },
  streefkerk: {
    alt: "Taxi in Streefkerk op de dijk langs de Lek",
    title: "Taxi Streefkerk met vaste prijs",
    caption: "Streefkerk: ophalen aan de dijk, ook voor vroege vluchten.",
  },
  "nieuw-lekkerland": {
    alt: "Taxi in Nieuw-Lekkerland aan de dijk met een molen in de verte",
    title: "Taxi Nieuw-Lekkerland met vaste prijs",
    caption: "Nieuw-Lekkerland grenst aan Kinderdijk; wij rijden naar beide luchthavens.",
  },
  moordrecht: {
    alt: "Taxi in Moordrecht langs een vaart met bakstenen huizen",
    title: "Taxi Moordrecht met vaste prijs",
    caption: "Moordrecht ligt bij Gouda; ophalen gebeurt op uw eigen adres.",
  },
  zuidland: {
    alt: "Taxi in Zuidland in een dorpsstraat bij de kerk",
    title: "Taxi Zuidland met vaste prijs",
    caption: "Zuidland: rustig dorp op Voorne-Putten, vaste prijs naar de luchthaven.",
  },
  "krimpen-aan-de-lek": {
    alt: "Taxi in Krimpen aan de Lek op de dijk langs de rivier",
    title: "Taxi Krimpen aan de Lek met vaste prijs",
    caption: "Krimpen aan de Lek: ophalen aan de dijk of in het dorp zelf.",
  },
  numansdorp: {
    alt: "Taxi in Numansdorp bij de haven met zeilboten",
    title: "Taxi Numansdorp met vaste prijs",
    caption: "Numansdorp ligt aan het Hollandsch Diep, in de Hoeksche Waard.",
  },
  rockanje: {
    alt: "Taxi in Rockanje bij de duinen en de vuurtoren",
    title: "Taxi Rockanje met vaste prijs",
    caption: "Rockanje: ritten van vakantieadres of woonhuis naar Schiphol en Rotterdam.",
  },
  heenvliet: {
    alt: "Taxi in Heenvliet in een dorpsstraat bij de kerk",
    title: "Taxi Heenvliet met vaste prijs",
    caption: "Heenvliet ligt op Voorne-Putten; de prijs staat vooraf vast.",
  },
  "hardinxveld-giessendam": {
    alt: "Taxi in Hardinxveld-Giessendam langs de Merwede",
    title: "Taxi Hardinxveld-Giessendam met vaste prijs",
    caption: "Hardinxveld-Giessendam: ophalen langs de rivier of in de woonwijken.",
  },
  puttershoek: {
    alt: "Taxi in Puttershoek bij de haven met de brug",
    title: "Taxi Puttershoek met vaste prijs",
    caption: "Puttershoek ligt aan de Oude Maas in de Hoeksche Waard.",
  },
  zwartewaal: {
    alt: "Taxi in Zwartewaal langs de haven met de dorpskerk",
    title: "Taxi Zwartewaal met vaste prijs",
    caption: "Zwartewaal: klein dorp, dezelfde vaste prijzen als elders.",
  },
  heerjansdam: {
    alt: "Taxi in Heerjansdam aan het water met bomen langs de straat",
    title: "Taxi Heerjansdam met vaste prijs",
    caption: "Heerjansdam ligt bij Zwijndrecht; ophalen voor de deur.",
  },
  hekelingen: {
    alt: "Taxi in Hekelingen op een landelijke weg met weilanden",
    title: "Taxi Hekelingen met vaste prijs",
    caption: "Hekelingen is landelijk gelegen bij Spijkenisse; ophalen op uw adres.",
  },
  "hoek-van-holland": {
    alt: "Taxi in Hoek van Holland met de duinen en de zee",
    title: "Taxi Hoek van Holland met vaste prijs",
    caption: "Hoek van Holland: ritten van de kust naar Schiphol en de ferryterminal.",
  },
  "hoogvliet-rotterdam": {
    alt: "Taxi in Hoogvliet Rotterdam in een woonwijk met een molen in de polder",
    title: "Taxi Hoogvliet Rotterdam met vaste prijs",
    caption: "Hoogvliet Rotterdam: ophalen in de wijk of bij Station Hoogvliet.",
  },
};

/** Gemeenten met een eigen afbeelding (/taxi/gemeente/<slug>). */
export const GEMEENTE_IMAGE_COPY: Record<string, ImageCopy> = {
  dordrecht: {
    alt: "Taxivervoer in de gemeente Dordrecht met het eiland en de rivieren",
    title: "Taxi gemeente Dordrecht",
    caption: "In de hele gemeente Dordrecht rijden wij met dezelfde vaste prijs.",
  },
  gouda: {
    alt: "Taxivervoer in de gemeente Gouda met polderlandschap rond de stad",
    title: "Taxi gemeente Gouda",
    caption: "De gemeente Gouda: van binnenstad tot Goverwelle en Achterwillens.",
  },
  rotterdam: {
    alt: "Taxivervoer in de gemeente Rotterdam met haven en woonwijken",
    title: "Taxi gemeente Rotterdam",
    caption: "Van Hoek van Holland tot Prins Alexander: één vaste prijs per rit.",
  },
  delft: {
    alt: "Taxivervoer in de gemeente Delft met grachten en campus",
    title: "Taxi gemeente Delft",
    caption: "De gemeente Delft omvat ook Tanthof en de campus; overal vaste prijzen.",
  },
  leiden: {
    alt: "Taxivervoer in de gemeente Leiden met grachten en stadsranden",
    title: "Taxi gemeente Leiden",
    caption: "Van de Merenwijk tot het Bio Science Park rijden wij vanaf de deur.",
  },
  schiedam: {
    alt: "Taxivervoer in de gemeente Schiedam met molens en woonwijken",
    title: "Taxi gemeente Schiedam",
    caption: "De gemeente Schiedam ligt op tien minuten van Rotterdam The Hague Airport.",
  },
  vlaardingen: {
    alt: "Taxivervoer in de gemeente Vlaardingen langs de Nieuwe Maas",
    title: "Taxi gemeente Vlaardingen",
    caption: "In Vlaardingen rijden wij ook 's nachts, met dezelfde vaste prijs.",
  },
  westland: {
    alt: "Taxivervoer in de gemeente Westland met glastuinbouw en dorpen",
    title: "Taxi gemeente Westland",
    caption: "Naaldwijk, Monster, Wateringen: elk dorp in het Westland heeft vaste tarieven.",
  },
  krimpenerwaard: {
    alt: "Taxivervoer in de gemeente Krimpenerwaard met weilanden en dijken",
    title: "Taxi gemeente Krimpenerwaard",
    caption: "De Krimpenerwaard is landelijk; ophalen gebeurt altijd op uw eigen adres.",
  },
  "hoeksche-waard": {
    alt: "Taxivervoer in de gemeente Hoeksche Waard met dijken en akkers",
    title: "Taxi gemeente Hoeksche Waard",
    caption: "Van Oud-Beijerland tot Numansdorp rijden wij naar Schiphol en Rotterdam.",
  },
};

function build(fileBase: string, copy: ImageCopy): LocationImage {
  return {
    src: `/city/${fileBase}-1200.webp`,
    srcSet: [480, 800, 1200].map((w) => `/city/${fileBase}-${w}.webp ${w}w`).join(", "),
    alt: copy.alt,
    title: copy.title,
    caption: copy.caption,
    width: WIDTH,
    height: HEIGHT,
  };
}

/** Unieke afbeelding voor een stadspagina, of null als die (nog) ontbreekt. */
export function cityImage(slug: string): LocationImage | null {
  const copy = CITY_IMAGE_COPY[slug];
  return copy ? build(`taxi-${slug}`, copy) : null;
}

/** Unieke afbeelding voor een gemeentepagina, of null als die (nog) ontbreekt. */
export function gemeenteImage(slug: string): LocationImage | null {
  const copy = GEMEENTE_IMAGE_COPY[slug];
  return copy ? build(`taxi-gemeente-${slug}`, copy) : null;
}

/** Absolute URL voor og:image/twitter:image. */
export function absoluteImageUrl(image: LocationImage, site = "https://taxiritje.nl") {
  return `${site}${image.src}`;
}
